import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { UtensilsCrossed, Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'

type LoginForm = {
  identifier: string
  password: string
}

type RegisterForm = {
  email: string
  password: string
  confirmPassword: string
}

export default function LoginPage() {
  const { t } = useTranslation()
  const { login, register: registerUser, isAuthenticated, isLoading } = useAuth()
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [showPassword, setShowPassword] = useState(false)
  const [shake, setShake] = useState(false)

  const loginSchema = z.object({
    identifier: z.string().min(1, t('auth.emailRequired')),
    password: z.string().min(1, t('auth.passwordRequired')),
  })

  const registerSchema = z.object({
    email: z.string().email(t('auth.invalidEmail')),
    password: z.string().min(6, t('auth.passwordMin')),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('auth.passwordsMismatch'),
    path: ['confirmPassword'],
  })

  const loginForm = useForm<LoginForm>({ resolver: zodResolver(loginSchema) })
  const registerForm = useForm<RegisterForm>({ resolver: zodResolver(registerSchema) as any })

  if (isAuthenticated) return <Navigate to="/dashboard" replace />

  const onLogin = async (data: LoginForm) => {
    try {
      await login(data)
    } catch {
      setShake(true)
      setTimeout(() => setShake(false), 500)
    }
  }

  const onRegister = async (data: any) => {
    try {
      const autoUsername = data.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '') + Date.now()
      await registerUser({ username: autoUsername, email: data.email, password: data.password } as any)
    } catch {
      setShake(true)
      setTimeout(() => setShake(false), 500)
    }
  }

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-[var(--color-bg-base)]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(232,168,56,0.08),transparent_70%)]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const }}
        className="relative z-10 w-full max-w-md px-4"
      >
        <motion.div
          animate={shake ? { x: [0, -10, 10, -10, 10, -5, 5, 0] } : {}}
          transition={{ duration: 0.5 }}
          className="rounded-[var(--radius-xl)] bg-[var(--color-bg-surface)] border border-[var(--color-border-subtle)] shadow-2xl shadow-black/20 p-8"
        >
          <div className="flex flex-col items-center mb-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-accent-amber-dim)] mb-4">
              <UtensilsCrossed className="h-7 w-7 text-[var(--color-accent-amber)]" />
            </div>
            <h1 className="font-display text-2xl font-bold text-[var(--color-text-primary)]">
              {mode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}
            </h1>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              {mode === 'login' ? t('auth.signInSubtitle') : t('auth.getStarted')}
            </p>
          </div>

          {/* Mode toggle */}
          <div className="flex rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-1 mb-6">
            <button
              type="button"
              onClick={() => setMode('login')}
              className={`flex-1 py-2 text-sm font-medium rounded-[var(--radius-sm)] transition-colors cursor-pointer ${
                mode === 'login'
                  ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
              }`}
            >
              {t('auth.signIn')}
            </button>
            <button
              type="button"
              onClick={() => setMode('register')}
              className={`flex-1 py-2 text-sm font-medium rounded-[var(--radius-sm)] transition-colors cursor-pointer ${
                mode === 'register'
                  ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
              }`}
            >
              {t('auth.createAccountBtn')}
            </button>
          </div>

          {mode === 'login' ? (
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
              <Input
                label={t('auth.email')}
                type="email"
                placeholder="you@restaurant.com"
                error={loginForm.formState.errors.identifier?.message}
                {...loginForm.register('identifier')}
              />
              <div className="relative">
                <Input
                  label={t('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  error={loginForm.formState.errors.password?.message}
                  {...loginForm.register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[34px] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button type="submit" className="w-full" loading={isLoading} size="lg">
                {t('auth.signIn')}
              </Button>
            </form>
          ) : (
            <form onSubmit={registerForm.handleSubmit(onRegister)} className="space-y-4">
              <Input
                label={t('auth.email')}
                type="email"
                placeholder="you@restaurant.com"
                error={registerForm.formState.errors.email?.message}
                {...registerForm.register('email')}
              />
              <div className="relative">
                <Input
                  label={t('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 6 characters"
                  error={registerForm.formState.errors.password?.message}
                  {...registerForm.register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[34px] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Input
                label={t('auth.confirmPassword')}
                type="password"
                placeholder="Re-enter your password"
                error={(registerForm.formState.errors as any).confirmPassword?.message}
                {...registerForm.register('confirmPassword')}
              />
              <Button type="submit" className="w-full" loading={isLoading} size="lg">
                {t('auth.createAccountBtn')}
              </Button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </div>
  )
}
